import type { FC } from 'react';
import dp10 from './../../../resources/content/dp10.png';
import { SubsequenceWrapper } from './Subsequence.styled';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface SubsequenceProps {}

const Subsequence: FC<SubsequenceProps> = () => (
 <SubsequenceWrapper>
    <header className="entry-header">
      <h1 className="entry-title" itemProp="headline">Subsequence</h1>			
   </header>
   <br/>
   <br/>
   <div className="entry-content clear" itemProp="text"  style={{height: 'auto !important'}}>
      <p className="text-align-center">A subsequence is formed by deleting some or no characters from the original string while preserving the order of the remaining characters.</p>
      
      <figure className="aligncenter size-large">
        <img decoding="async" width="775" height="400" src={dp10} alt="" className="lazy-hidden" sizes="auto, (max-width: 775px) 100vw, 775px" />
      </figure>
      
      <ul className="text-align-justify">
        <li className="linear"><strong>Break Down Problem</strong></li>
        <ul className="text-align-justify">
          <li>The first step is to divide complex problems into smaller subproblems. Think of this like peeling an onion ~ each layer reveals a simpler version of the original challenge. When dealing with subsequences, we often break down strings or arrays into smaller segments that we can analyze independently.</li>
        </ul>
        <li className="linear"><strong>Solve Subproblems</strong></li>
        <ul className="text-align-justify">
          <li>Next, we address each subproblem individually. This is where the magic of dynamic programming shines. Instead of solving the same subproblem multiple times, we tackle each one systematically and build our solution step by step.</li>
        </ul>
        <li className="linear"><strong>Store Solutions</strong></li>
        <ul className="text-align-justify">
          <li>Here's where efficiency comes into play ~ we save results to avoid repetition. This memoization technique is crucial for transforming exponential time algorithms into polynomial-time solutions. Every solved subproblem becomes a building block for larger problems.</li>
        </ul>
        <li className="linear"><strong>Utilize Table</strong></li>
        <ul className="text-align-justify">
          <li>Finally, we use a table for efficient tracking. This tabular approach, often called "bottom-up" dynamic programming, allows us to visualize our progress and ensures we don't miss any dependencies between subproblems.</li>
        </ul>
      </ul>
      <br/>
      <h4 className="text-align-left">Longest Common Subsequence</h4>
      <ul className="text-align-justify">
        <li>We're looking for the overlap between the two strings ~ the characters that exist in both while maintaining their sequence.</li>
        <li>Important points to note:</li>
        <ul className="text-align-justify">
          <li>Characters must be in order</li>
          <li>They don’t need to be continuous</li>
        </ul>
        <li>The goal is to find the length of the longest subsequence that appears in both strings.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'text1 = "abc", text2 = "ac"\n'+
            '\n'+
            'Compare:\n'+
            '	a == a → MATCH → +1\n'+
            '	b != c → NO MATCH → Either skip \'b\' or \'c\', means carry forward the max(0,1)\n'+
            '	c == c → MATCH → +1\n'+
            '	\n'+
            '👉 Answer is 2'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Edit Distance</h4>
      <ul className="text-align-justify">
        <li>At each position in both strings, we have three choices:</li>
        <ul className="text-align-justify">
          <li>If characters match, no operation needed</li>
          <li>If they don't match, we can:</li>
          <ul className="text-align-justify">
            <li>Insert a character</li>
            <li>Delete a character</li>
            <li>Replace a character</li>
          </ul>
        </ul>
        <li>The goal is to calculate the minimum number of operations needed to transform one string into another.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Convert "cat" → "cut"\n'+
            '\n'+
            'Compare:\n'+
            '	c == c → no cost\n'+
            '	a != u → replace → +1 for the operation performed\n'+
            '	t == t → no cost\n'+
            '	\n'+
            '👉 Total = 1'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Longest Palindromic Subsequence</h4>
      <ul className="text-align-justify">
        <li>A palindrome is a string that reads the same forward and backward. Like 'level', same from both ends.</li>
        <li>The goal is to find the length of the longest palindromic subsequence in a string.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            'Say, s = "bbab"\n'+
            '\n'+
            'Check:\n'+
            '	b == b → match (+2)\n'+
            '	b != a → best palindrome = (+1)\n'+
            '	\n'+
            '👉  Answer = 1 + 2 → 3 ("bab" or "bbb")'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      <h4 className="text-align-left">Distinct Subsequences</h4>
      <ul className="text-align-justify">
        <li>Important points to note:</li>
        <ul className="text-align-justify">
          <li>Order must be maintained</li>
          <li>You can skip characters in s</li>
          <li>There is 1 way to form empty string (do nothing)</li>
        </ul>
        <li>The goal is to count the number of ways to form one string as a subsequence of another.</li>
        <li>Let's take an example:</li>
        <SyntaxHighlighter language="java" style={dracula} showLineNumbers={true}>
          { 
            's = "rabbbit", t = "rabbit"\n'+
            '\n'+
            'There are 3 ways to delete one *b* from "rabbbit" and form "rabbit".\n'+
            'No.of ways can you form t from s is 3. \n'+
            '\n'+
            '👉 Answer is 3'
          }
        </SyntaxHighlighter>
      </ul>
      <br/>
      
   </div>
 </SubsequenceWrapper>
);

export default Subsequence;
